import React, { useEffect, useState } from "react";
import { collection, getDocs, query, orderBy, updateDoc, deleteDoc, doc, Timestamp } from "firebase/firestore";
import { db } from "../../firebase";

const COLLECTION = "egitimBasvurulari";

type Durum = "bekliyor" | "onaylandi";
type Tur = "ders_almak" | "ders_vermek" | "sponsor";

type EvetHayir = "" | "evet" | "hayir";

interface Basvuru {
  id: string;
  tur: Tur;
  ad: string;
  telefon: string;
  email: string;
  not: string;
  tarih: Timestamp | null;
  durum: Durum;
  // Sadece "ders_almak" başvurularında dolu (Öğrenci Bilgi Formu):
  diplomaNotu?: string;
  okulOrtalamasi?: string;
  kardesSayisi?: string;
  anneCalisiyor?: EvetHayir;
  babaCalisiyor?: EvetHayir;
  aileGeliri?: string;
  evKira?: EvetHayir;
  dernekUyesi?: EvetHayir;
  odemeDurumu?: "" | "odeyebilirim" | "odeyemem";
  // Sadece "ders_vermek" başvurularında dolu (Öğretmen Bilgi Formu):
  universite?: string;
  deneyimYili?: string;
  ucretTuru?: "" | "gonullu" | "ucretli";
  saatlikUcret?: string;
  // Sadece "sponsor" başvurularında dolu:
  sponsorTuru?: "" | "materyal" | "ogretmen_ucreti" | "aylik_bagis";
  tutar?: string;
}

const EVET_HAYIR_LABEL: Record<EvetHayir, string> = { "": "—", evet: "Evet", hayir: "Hayır" };
const ODEME_LABEL: Record<string, string> = { odeyebilirim: "Ödeyebilirim", odeyemem: "Ödeyemem" };
const UCRET_TURU_LABEL: Record<string, string> = { gonullu: "Gönüllü", ucretli: "Ücret Talep Ediyor" };
const SPONSOR_TURU_LABEL: Record<string, string> = {
  materyal: "Eğitim materyallerini karşılamak istiyor",
  ogretmen_ucreti: "Öğretmen ücretini karşılamak istiyor",
  aylik_bagis: "Aylık bağış yapabilir",
};

const TUR_LABELS: Record<Tur, { label: string; color: string; icon: string }> = {
  ders_almak: { label: "Ders Almak İstiyor", color: "bg-blue-100 text-blue-700", icon: "fa-user-graduate" },
  ders_vermek: { label: "Ders Vermek İstiyor", color: "bg-purple-100 text-purple-700", icon: "fa-chalkboard-user" },
  sponsor: { label: "Sponsor Olmak İstiyor", color: "bg-amber-100 text-amber-700", icon: "fa-hand-holding-heart" },
};

const STATUS_LABELS: Record<Durum, { label: string; color: string }> = {
  bekliyor: { label: "Bekliyor", color: "bg-yellow-100 text-yellow-700" },
  onaylandi: { label: "Onaylandı", color: "bg-green-100 text-green-700" },
};

function formatTarih(t: Timestamp | null): string {
  if (!t || typeof t.toDate !== "function") return "—";
  try { return t.toDate().toLocaleDateString("tr-TR"); } catch { return "—"; }
}

const EgitimBasvurulariPage: React.FC = () => {
  const [rows, setRows] = useState<Basvuru[]>([]);
  const [loading, setLoading] = useState(true);
  const [hata, setHata] = useState(false);
  const [turFilter, setTurFilter] = useState<string>("hepsi");
  const [durumFilter, setDurumFilter] = useState<string>("hepsi");
  const [selected, setSelected] = useState<Basvuru | null>(null);

  const load = async () => {
    setLoading(true);
    setHata(false);
    try {
      const snap = await getDocs(query(collection(db, COLLECTION), orderBy("tarih", "desc")));
      setRows(snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })) as Basvuru[]);
    } catch (e) {
      console.error("Eğitim başvuruları okunamadı:", e);
      setHata(true);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = rows.filter(
    (r) => (turFilter === "hepsi" || r.tur === turFilter) && (durumFilter === "hepsi" || r.durum === durumFilter)
  );

  const onayla = async (id: string) => {
    try {
      await updateDoc(doc(db, COLLECTION, id), { durum: "onaylandi" });
      setRows((prev) => prev.map((r) => (r.id === id ? { ...r, durum: "onaylandi" } : r)));
    } catch (e) {
      console.error("Durum güncellenemedi:", e);
      window.alert("Durum güncellenemedi, lütfen tekrar deneyin.");
    }
  };

  const remove = async (id: string) => {
    if (!window.confirm("Bu başvuru silinsin mi?")) return;
    try {
      await deleteDoc(doc(db, COLLECTION, id));
      setRows((prev) => prev.filter((r) => r.id !== id));
    } catch (e) {
      console.error("Silinemedi:", e);
      window.alert("Silme başarısız, lütfen tekrar deneyin.");
    }
  };

  const bekleyen = rows.filter((r) => r.durum === "bekliyor").length;

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="text-xl font-bold text-gray-800">Eğitim Başvuruları</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            {bekleyen} bekleyen, {rows.length} toplam · Ana Sayfa "Geleceğimiz İçin Eğitim" formlarından
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {["hepsi", "bekliyor", "onaylandi"].map((f) => (
            <button key={f} onClick={() => setDurumFilter(f)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors
                ${durumFilter === f ? "bg-[#8B1428] text-white" : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-50"}`}>
              {f === "hepsi" ? "Hepsi" : STATUS_LABELS[f as Durum]?.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex gap-2 flex-wrap mb-6">
        {["hepsi", "ders_almak", "ders_vermek", "sponsor"].map((t) => (
          <button key={t} onClick={() => setTurFilter(t)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors
              ${turFilter === t ? "bg-gray-800 text-white" : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-50"}`}>
            {t === "hepsi" ? "Tüm Türler" : TUR_LABELS[t as Tur]?.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="py-20 text-center text-gray-400"><i className="fa-solid fa-circle-notch fa-spin text-3xl" /></div>
      ) : hata ? (
        <div className="bg-red-50 text-red-600 rounded-2xl py-12 text-center text-sm">
          Başvurular yüklenemedi. <button onClick={load} className="underline font-semibold">Tekrar dene</button>
        </div>
      ) : rows.length === 0 ? (
        <div className="bg-white rounded-2xl border border-dashed border-gray-300 py-20 text-center">
          <i className="fa-solid fa-graduation-cap text-5xl text-gray-300 mb-4" />
          <p className="text-gray-500 font-medium">Henüz eğitim başvurusu yok</p>
          <p className="text-gray-400 text-sm mt-1">Ana sayfadaki eğitim formlarından biri doldurulduğunda burada görünecek</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Tür</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Ad</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase hidden md:table-cell">Telefon</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase hidden sm:table-cell">E-posta</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase hidden lg:table-cell">Özet</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase hidden md:table-cell">Tarih</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Durum</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">İşlem</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map((r) => (
                <tr key={r.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3">
                    <span className={`text-xs font-semibold px-2 py-1 rounded-full whitespace-nowrap ${TUR_LABELS[r.tur]?.color ?? "bg-gray-100 text-gray-600"}`}>
                      <i className={`fa-solid ${TUR_LABELS[r.tur]?.icon} mr-1`} />
                      {TUR_LABELS[r.tur]?.label ?? r.tur}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-medium text-gray-800">
                    <button onClick={() => setSelected(r)} className="hover:text-[#8B1428] text-left">
                      {r.ad}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-gray-500 font-mono text-xs hidden md:table-cell">{r.telefon || "—"}</td>
                  <td className="px-4 py-3 text-gray-500 text-xs hidden sm:table-cell">{r.email || "—"}</td>
                  <td className="px-4 py-3 text-gray-500 text-xs hidden lg:table-cell max-w-xs truncate">
                    {r.tur === "ders_almak" && (ODEME_LABEL[r.odemeDurumu ?? ""] ?? "—")}
                    {r.tur === "ders_vermek" && (UCRET_TURU_LABEL[r.ucretTuru ?? ""] ?? "—")}
                    {r.tur === "sponsor" && (SPONSOR_TURU_LABEL[r.sponsorTuru ?? ""] ?? "—")}
                  </td>
                  <td className="px-4 py-3 text-gray-400 text-xs hidden md:table-cell">{formatTarih(r.tarih)}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-semibold px-2 py-1 rounded-full ${STATUS_LABELS[r.durum]?.color ?? "bg-gray-100 text-gray-600"}`}>
                      {STATUS_LABELS[r.durum]?.label ?? r.durum}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {r.durum === "bekliyor" && (
                        <button onClick={() => onayla(r.id)} className="text-green-600 hover:text-green-800 text-xs font-semibold">Onayla</button>
                      )}
                      <button onClick={() => remove(r.id)} className="text-gray-400 hover:text-red-500 transition-colors"><i className="fa-solid fa-trash text-xs" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 text-xs text-gray-500">{filtered.length} kayıt gösteriliyor</div>
        </div>
      )}

      {/* Başvuru detay modalı (türe göre farklı alanlar) */}
      {selected && (
        <>
          <div className="fixed inset-0 bg-black/40 z-40" onClick={() => setSelected(null)} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-8 relative">
              <button onClick={() => setSelected(null)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-700">
                <i className="fa-solid fa-xmark text-lg" />
              </button>
              <h3 className="text-lg font-bold text-gray-800 mb-1">{selected.ad}</h3>
              <p className="text-xs text-gray-400 mb-5">{TUR_LABELS[selected.tur]?.label} · {formatTarih(selected.tarih)}</p>

              {selected.tur === "ders_almak" && (
                <div className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm mb-5">
                  <div><div className="text-xs text-gray-400 mb-0.5">Telefon</div><div className="text-gray-700 font-medium">{selected.telefon || "—"}</div></div>
                  <div><div className="text-xs text-gray-400 mb-0.5">E-posta</div><div className="text-gray-700 font-medium">{selected.email || "—"}</div></div>
                  <div><div className="text-xs text-gray-400 mb-0.5">Diploma Notu</div><div className="text-gray-700 font-medium">{selected.diplomaNotu || "—"}</div></div>
                  <div><div className="text-xs text-gray-400 mb-0.5">Okul Ortalaması</div><div className="text-gray-700 font-medium">{selected.okulOrtalamasi || "—"}</div></div>
                  <div><div className="text-xs text-gray-400 mb-0.5">Kardeş Sayısı</div><div className="text-gray-700 font-medium">{selected.kardesSayisi || "—"}</div></div>
                  <div><div className="text-xs text-gray-400 mb-0.5">Aile Geliri</div><div className="text-gray-700 font-medium">{selected.aileGeliri || "—"}</div></div>
                  <div><div className="text-xs text-gray-400 mb-0.5">Anne Çalışıyor mu?</div><div className="text-gray-700 font-medium">{EVET_HAYIR_LABEL[selected.anneCalisiyor ?? ""]}</div></div>
                  <div><div className="text-xs text-gray-400 mb-0.5">Baba Çalışıyor mu?</div><div className="text-gray-700 font-medium">{EVET_HAYIR_LABEL[selected.babaCalisiyor ?? ""]}</div></div>
                  <div><div className="text-xs text-gray-400 mb-0.5">Ev Kira mı?</div><div className="text-gray-700 font-medium">{EVET_HAYIR_LABEL[selected.evKira ?? ""]}</div></div>
                  <div><div className="text-xs text-gray-400 mb-0.5">Dernek Üyesi mi?</div><div className="text-gray-700 font-medium">{EVET_HAYIR_LABEL[selected.dernekUyesi ?? ""]}</div></div>
                  <div className="col-span-2">
                    <div className="text-xs text-gray-400 mb-0.5">Aylık 2000 TL Katkı Payı</div>
                    <div className={`font-semibold ${selected.odemeDurumu === "odeyebilirim" ? "text-green-700" : "text-red-600"}`}>
                      {ODEME_LABEL[selected.odemeDurumu ?? ""] ?? "—"}
                    </div>
                  </div>
                </div>
              )}

              {selected.tur === "ders_vermek" && (
                <div className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm mb-5">
                  <div><div className="text-xs text-gray-400 mb-0.5">Telefon</div><div className="text-gray-700 font-medium">{selected.telefon || "—"}</div></div>
                  <div><div className="text-xs text-gray-400 mb-0.5">E-posta</div><div className="text-gray-700 font-medium">{selected.email || "—"}</div></div>
                  <div className="col-span-2"><div className="text-xs text-gray-400 mb-0.5">Mezun Olduğu Üniversite</div><div className="text-gray-700 font-medium">{selected.universite || "—"}</div></div>
                  <div><div className="text-xs text-gray-400 mb-0.5">Deneyim</div><div className="text-gray-700 font-medium">{selected.deneyimYili || "—"}</div></div>
                  <div><div className="text-xs text-gray-400 mb-0.5">Gönüllü / Ücretli</div><div className="text-gray-700 font-medium">{UCRET_TURU_LABEL[selected.ucretTuru ?? ""] ?? "—"}</div></div>
                  {selected.ucretTuru === "ucretli" && (
                    <div className="col-span-2">
                      <div className="text-xs text-gray-400 mb-0.5">Saatlik Ücret Talebi</div>
                      <div className="text-gray-700 font-semibold">{selected.saatlikUcret || "—"}</div>
                    </div>
                  )}
                </div>
              )}

              {selected.tur === "sponsor" && (
                <div className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm mb-5">
                  <div><div className="text-xs text-gray-400 mb-0.5">Telefon</div><div className="text-gray-700 font-medium">{selected.telefon || "—"}</div></div>
                  <div><div className="text-xs text-gray-400 mb-0.5">E-posta</div><div className="text-gray-700 font-medium">{selected.email || "—"}</div></div>
                  <div className="col-span-2">
                    <div className="text-xs text-gray-400 mb-0.5">Destek Türü</div>
                    <div className="text-gray-700 font-medium">{SPONSOR_TURU_LABEL[selected.sponsorTuru ?? ""] ?? "—"}</div>
                  </div>
                  <div className="col-span-2">
                    <div className="text-xs text-gray-400 mb-0.5">Aylık Ödeyebileceği Tutar</div>
                    <div className="text-green-700 font-semibold">{selected.tutar ? `${selected.tutar} TL` : "—"}</div>
                  </div>
                </div>
              )}

              <div className="flex gap-3">
                {selected.durum === "bekliyor" && (
                  <button
                    onClick={() => { onayla(selected.id); setSelected({ ...selected, durum: "onaylandi" }); }}
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold py-2.5 rounded-lg"
                  >
                    Onayla
                  </button>
                )}
                <button
                  onClick={() => { remove(selected.id); setSelected(null); }}
                  className="flex-1 border border-red-200 text-red-500 hover:bg-red-50 text-sm font-semibold py-2.5 rounded-lg"
                >
                  Sil
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default EgitimBasvurulariPage;
